import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { actions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { selectBackendTemplates } from "../../pages/Applications";
import { 
  fetchEndpoints, 
  fetchServices, 
  fetchCollections, 
  fetchBackgroundTasks, 
  deleteDataModel, 
  updateDataModel, 
  selectSelectedDataModel, 
  fetchENVSecrets} from "./index";
import {
  STATUS,
  ROUTES,
  REGEX,
  API_RESPONSES,
  EMPTY_STRING,
  EMPTY_ARRAY,
  VALIDATION_MESSAGES, 
  ALERT_MESSAGES
} from "../../constants";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { GLOBAL_INFRASTRUCTURE_REGIONS } from "../../components/organisms/AppDrawers/drawers.constants";
import Alert, { selectAlert } from "../../components/organisms/Alert";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { renderNoDataModelLayout } from "../../helpers";

const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const { ACTIVE, INACTIVE } = STATUS;
const { DATAMODELS } = ROUTES;
const { setSelectedDataModelId } = actions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;
const { SUCCESS } = API_RESPONSES;
const { DELETE_DATA_MODEL, UPDATE_DATA_MODEL, FETCH_DATA_MODELS } =
  SPINNER_NAMES;

const ACTIVE_DATAMODEL = "Active";
const INACTIVE_DATAMODEL = "Inactive";
const { NAME_SHOULD_CONTAIN, NAME_SHOULD_NOT_CONTAIN, MIN_CHAR_LENGTH, MAX_CHAR_LENGTH_TWENTY_SEVEN, 
  NAME_SHOULD_NOT_CONTAIN_AT_END, DATA_MODAL_NAME } = VALIDATION_MESSAGES

const { DATAMODEL_UPDATED } = ALERT_MESSAGES

const DEFAULT_VALUES = {
  name: EMPTY_STRING,
  region: EMPTY_STRING,
  status: false,
  isEnabledRealTime: false,
  isEnabledRLS: false,
  template: EMPTY_STRING
};

const DataModelSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dataModelId } = useParams();
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const predefinedTemplates = useSelector(selectBackendTemplates);

  const selectedAlert = useSelector(selectAlert);

  const [currentDataModel, setCurrentDataModel] = useState(DEFAULT_VALUES);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [templates, setTemplates] = useState(EMPTY_ARRAY);

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(
        SPECIAL_CHARACTERS,
        NAME_SHOULD_NOT_CONTAIN
      )
      .min(3, MIN_CHAR_LENGTH)
      .max(27, MAX_CHAR_LENGTH_TWENTY_SEVEN)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(DATA_MODAL_NAME),
  });
  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (predefinedTemplates) setTemplates(predefinedTemplates);
  }, [predefinedTemplates]);

  useEffect(() => {
    if (selectedDataModel) {
      dispatch(setSelectedTab(selectedDataModel));
      setCurrentDataModel({
        ...selectedDataModel,
        status: selectedDataModel.status === ACTIVE,
      });
    }
  }, [dispatch, selectedDataModel]);

  useEffect(() => {
    if (dataModelId && !selectedDataModel)
      dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId, selectedDataModel]);

  const handleOnSubmit = useCallback(
    (values) => {
      values = { ...values, status: values.status ? ACTIVE : INACTIVE };
      const updatedDataModel = { ...currentDataModel, ...values };

      delete updatedDataModel.collections;
      delete updatedDataModel.backgroundTasks;
      delete updatedDataModel.trackDatabaseSetup;
      delete updatedDataModel.endpoints;
      delete updatedDataModel.endpoints;
      delete updatedDataModel.services;
      delete updatedDataModel.applications;

      dispatch(
        updateDataModel({
          id: selectedDataModel.id,
          dataModel: {
            dataModel: selectedDataModel,
            changeSet: updatedDataModel,
          },
        })
      ).then((res) => {
        if (res.payload) {
          const { id } = selectedDataModel;
          dispatch(fetchEndpoints({ id }));
          dispatch(fetchServices({ id }));
          dispatch(fetchBackgroundTasks({ id }));
          dispatch(fetchCollections({ id }));
          dispatch(fetchENVSecrets(id));
          setAlertData({
            type: SUCCESS,
            message: DATAMODEL_UPDATED,
          });
        }
      });
    },
    [dispatch, currentDataModel, selectedDataModel]
  );

  const handleDeleteDataModel = useCallback(() => {
    dispatch(deleteDataModel(currentDataModel.id)).then((res) => {
      if (res.payload) {
        dispatch(setSelectedModal(null));
        setShouldShowDeleteModal(false);
        navigate(DATAMODELS, { replace: true });
      }
    });
  }, [dispatch, navigate, currentDataModel]);

  return (
    <div className="scrollbar content overflow-auto p-6 flex-1">
      <Helmet>
        <title>Settings | TavaJS</title>
      </Helmet>
      <Spinner name={FETCH_DATA_MODELS}>
        { selectedDataModel ? (
        <>
        <div className="panel border border-gray-700 mb-4 bg-gray-800 rounded-lg ">
          <div className="panel-header p-6 border-b border-gray-700">
            <h6 className="text-base font-bold text-gray-50">
              Database Settings
            </h6>
            {alertData && (
              <div className="my-2">
                <Alert
                  type={alertData.type}
                  message={alertData.message}
                  handleOnClose={setAlertData}
                />
              </div>
            )}
          </div>
          <Formik
            enableReinitialize
            initialValues={currentDataModel}
            validationSchema={validationSchema}
            onSubmit={handleOnSubmit}
          >
            {({
              values,
              touched,
              handleChange,
              setFieldValue,
              errors,
              dirty,
            }) => {
              return (
                <Form>
                  <div className="panel-body p-6 ">
                    <div className="space-y-4 mb-6">
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-50 mb-1 block">
                          Database Name
                        </label>
                        <Field
                          name="name"
                          type="text"
                          className="form-control disabled:opacity-75"
                          placeholder="Database Name"
                          value={values.name}
                          onChange={handleChange}
                          disabled={true}
                        />
                        {touched.name && errors.name && (
                          <div className="text-sm font-medium text-red-500">
                            {errors.name}
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-50 mb-1 block">
                          Region
                        </label>
                        <select
                          name="region"
                          value={values.region}
                          onChange={handleChange}
                          className="form-control"
                        >
                          {GLOBAL_INFRASTRUCTURE_REGIONS.map((each) => (
                            <option key={each.value} value={each.value}>
                              {each.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-50 mb-1 block">
                          Template
                        </label>
                        <select
                          name="template"
                          value={values.template}
                          onChange={handleChange}
                          className="form-control"
                        >
                          {templates.map((each) => (
                            <option key={each.value} value={each.value}>
                              {each.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group ">
                        <h6 className="text-sm font-medium text-gray-50 mb-5">
                          Settings
                        </h6>
                        <label className="inline-flex cursor-pointer mr-auto md:mr-0 ml-auto mb-5">
                          <div className="inline-flex">
                            <input
                              type="checkbox"
                              name="status"
                              checked={values.status}
                              role="switch"
                              onChange={(e) =>
                                setFieldValue("status", e.target.checked)
                              }
                              className="hidden peer"
                            />
                            <div className="w-11 h-6 relative bg-gray-700 rounded-full peer-checked:after:translate-x-full peer-checked:after:border-white  after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-emerald-500"></div>
                          </div>
                          <div className="ml-3 text-sm">
                            <p className="font-medium text-gray-50 dark:text-gray-300">
                              {values.status
                                ? ACTIVE_DATAMODEL
                                : INACTIVE_DATAMODEL}
                            </p>
                            <span className="text-gray-300 ">
                              Activate your data model and create an active
                              backend application
                            </span>
                          </div>
                        </label>
                        {/* <label className="text-gray-300 flex mb-5 lg:w-96">
                        <input
                          type="checkbox"
                          name="isEnabledRealTime"
                          onChange={handleChange}
                          checked={values.isEnabledRealTime}
                          className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
                        />
                        <div className="ml-3">
                          <div className="flex flex-row relative">
                            <h6 className="text-sm font-medium leading-4 mb-1">
                              Enable Realtime
                            </h6>
                            <Tooltip message={REALTIME} />
                           </div>
                          <span className="text-sm font-light leading-5 block">
                            Broadcast changes on this data collection to
                            authorized subscribers
                          </span>
                        </div>
                      </label>
                      <label className="text-gray-300 flex mb-5 lg:w-96">
                        <input
                          type="checkbox"
                          name="isEnabledRLS"
                          onChange={handleChange}
                          checked={values.isEnabledRLS}
                          className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
                        />
                        <div className="ml-3">
                          <div className="flex flex-row relative">
                            <h6 className="text-sm font-medium leading-4 mb-1">
                              Enable Row Level Security
                            </h6>
                             <Tooltip message={RLS} />
                          </div>
                          <span className="text-sm font-light leading-5 block">
                            Restrict access to your data model by enabling RLS
                            and writing Postgres policies. If RLS is not
                            enabled, anyone with the anon key can modify and
                            delete your data.
                          </span>
                        </div>
                      </label> */}
                      </div>
                    </div>
                    <div className="text-right">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSpinnerActive || !dirty}
                      >
                        <Spinner
                          name={UPDATE_DATA_MODEL}
                          setIsSpinnerActive={setIsSpinnerActive}
                          persistSize
                        >
                          Update
                        </Spinner>
                      </button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="panel border border-gray-700 bg-gray-800 rounded-lg h-20 mb-4">
          <div className="flex justify-between text-base font-bold text-gray-50 p-6">
            <span>Environment Secrets</span>
            <div className="flex justify-end space-x-2">
              <button
                className="btn btn-primary h-fit"
                onClick={() => {
                  dispatch(setSelectedModal(MODALS.ADD_SECRETS_MODAL));
                }}
              >
                Add New Secret
              </button>
              <button
                className="btn btn-primary h-fit"
                onClick={() => {
                  navigate(`/data-models/${dataModelId}/settings/secrets`);
                }}
              >
                Manage Secrets
              </button>
            </div>
          </div>
        </div>
        <div className="panel border border-gray-700  bg-gray-800 rounded-lg">
          <div className="panel-header p-6 border-b border-gray-700">
            <h6 className="text-base font-bold text-gray-50">
              Delete Database
            </h6>
            <span className="text-sm text-gray-50">
              This action cannot be undone.
            </span>
          </div>
          <div className="panel-body p-6">
            <div className="text-right">
              <button
                className="btn btn-danger h-fit"
                onClick={() => {
                  dispatch(setSelectedModal(MODALS.DELETE_MODAL));
                  dispatch(setItemToBeDeleted(currentDataModel));
                  setShouldShowDeleteModal(true);
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteDataModel}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Database"
            spinner={DELETE_DATA_MODEL}
          />
        )}
        </>
        ) : (
          renderNoDataModelLayout(navigate)
        )}
      </Spinner>
    </div>
  );
};

export default DataModelSettings;
